import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import type { Id } from 'react-calendar-timeline';
import Timeline, {
  DateHeader,
  TimelineHeaders,
  TimelineMarkers,
  TodayMarker,
} from 'react-calendar-timeline';
import { useIntl } from '@/core/i18n/use-intl';

import './timeline.scss';

export interface TimelineGroup {
  id: Id;
  title: ReactNode;
  root?: boolean;
  height?: number;
}

export interface TimelineItem {
  id: Id;
  group: Id;
  title: ReactNode;
  start_time: number;
  end_time: number;
  className?: string;
}

export interface TimelineViewProps {
  groups: TimelineGroup[];
  items: TimelineItem[];
  startTime: number; // Default start time
  endTime: number; // Default end time
  lineHeight?: number;
  sidebarWidth?: number;
  stackItems?: boolean;
  horizontalLineClassNamesForGroup?: (group: TimelineGroup) => string[];
  onItemClick?: (itemId: Id) => void;
}

export interface TimelineViewHandle {
  handleZoomIn: () => void;
  handleZoomOut: () => void;
}

const MIN_ZOOM_DEFAULT = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_ZOOM_DEFAULT = 3 * 365 * 24 * 60 * 60 * 1000; // 3 years

export const TimelineView = forwardRef<TimelineViewHandle, TimelineViewProps>(
  (
    {
      groups,
      items,
      startTime,
      endTime,
      lineHeight = 40,
      sidebarWidth = 300,
      stackItems = true,
      horizontalLineClassNamesForGroup,
      onItemClick,
    },
    ref
  ) => {
    const mouseDownRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const isDraggingRef = useRef(false);
    const dragThreshold = 5; // pixels
    const [timelineUnit, setTimelineUnit] = useState<string | null>(null);
    const [visibleTimeStart, setVisibleTimeStart] = useState<number>(startTime);
    const [visibleTimeEnd, setVisibleTimeEnd] = useState<number>(endTime);

    // Expose zoom controls via ref
    useImperativeHandle(ref, () => ({
      handleZoomIn: () => handleZoom(0.5),
      handleZoomOut: () => handleZoom(2),
    }));

    const handleZoom = (factor: number) => {
      const currentDuration = visibleTimeEnd - visibleTimeStart;
      let newDuration = currentDuration * factor;

      // Clamp newDuration to min/max zoom limits
      if (newDuration < MIN_ZOOM_DEFAULT) {
        newDuration = MIN_ZOOM_DEFAULT;
      }
      if (newDuration > MAX_ZOOM_DEFAULT) {
        newDuration = MAX_ZOOM_DEFAULT;
      }

      // Prevent zoom if duration hasn't changed (already at limit)
      // and the factor would not have changed it anyway
      if (newDuration === currentDuration && currentDuration * factor !== newDuration) {
        return;
      }

      const center = visibleTimeStart + currentDuration / 2;
      const newStartTime = center - newDuration / 2;
      const newEndTime = center + newDuration / 2;

      if (!Number.isNaN(newStartTime) && !Number.isNaN(newEndTime)) {
        setVisibleTimeStart(newStartTime);
        setVisibleTimeEnd(newEndTime);
      }
    };

    // Workaround for react-calendar-timeline's onItemSelect behavior.
    // onItemClick requires two clicks, which is not ideal UX.
    // onItemSelect is triggered on a single click, but also on drag.
    // This logic manually tracks mouse down, move, and up events to determine if a drag occurred.
    // If no drag occurred, the onItemClick (passed as onItemSelect) is triggered.
    const handleMouseDown = (e: React.MouseEvent) => {
      mouseDownRef.current = { x: e.clientX, y: e.clientY };
      isDraggingRef.current = false;

      // Add global event listeners that will track mouse movement and release
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const dx = Math.abs(e.clientX - mouseDownRef.current.x);
      const dy = Math.abs(e.clientY - mouseDownRef.current.y);

      if (dx > dragThreshold || dy > dragThreshold) {
        isDraggingRef.current = true;
      }
    };

    const handleMouseUp = () => {
      // Clean up event listeners
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      mouseDownRef.current = { x: 0, y: 0 };
    };

    const handleItemSelect = (itemId: Id) => {
      if (!isDraggingRef.current && onItemClick) {
        onItemClick(itemId);
      }
    };

    const handleTimeChange = (
      newVisibleTimeStart: number,
      newVisibleTimeEnd: number,
      updateScrollCanvas: (start: number, end: number) => void
    ) => {
      setVisibleTimeStart(newVisibleTimeStart);
      setVisibleTimeEnd(newVisibleTimeEnd);
      updateScrollCanvas(newVisibleTimeStart, newVisibleTimeEnd);
    };

    const handleTimelineZoom = (zoom: {
      timelineUnit: string;
      visibleTimeStart: number;
      visibleTimeEnd: number;
      canvasWidth: number;
      timelineWidth: number;
      canvasTimeStart: number;
      canvasTimeEnd: number;
    }) => {
      setTimelineUnit(zoom.timelineUnit);
    };

    const { locale } = useIntl();

    const visibleDuration = visibleTimeEnd - visibleTimeStart;
    const oneYearInMillis = 365 * 24 * 60 * 60 * 1000;
    const onePointFiveYearsInMillis = 1.5 * oneYearInMillis;

    let monthFormat = 'MMMM'; // Default to full month names
    if (visibleDuration > onePointFiveYearsInMillis) {
      monthFormat = 'LL'; // Two-letter month names (e.g., Ja, Fe)
    } else if (visibleDuration > oneYearInMillis) {
      monthFormat = 'MMM'; // Short month names (e.g., Jan, Feb)
    }

    return (
      <section
        className="rounded-lg border border-border overflow-hidden relative z-0"
        onMouseDown={handleMouseDown} // Attach mouse down listener to the container to detect drag initiation
        aria-label="Timeline"
      >
        <Timeline
          groups={groups}
          items={items}
          visibleTimeStart={visibleTimeStart}
          visibleTimeEnd={visibleTimeEnd}
          canMove={false}
          canResize={false}
          minZoom={MIN_ZOOM_DEFAULT}
          maxZoom={MAX_ZOOM_DEFAULT}
          lineHeight={lineHeight}
          sidebarWidth={sidebarWidth}
          stackItems={stackItems}
          horizontalLineClassNamesForGroup={horizontalLineClassNamesForGroup}
          onItemSelect={handleItemSelect} // Use our custom handler for item selection
          onTimeChange={handleTimeChange} // Handle timeline scroll/zoom
          onZoom={handleTimelineZoom}
          onItemClick={handleItemSelect}
        >
          <TimelineMarkers>
            <TodayMarker>
              {({ styles }) => {
                const customStyles = {
                  ...styles,
                  width: '1px',
                  backgroundColor: 'rgba(var(--primary))',
                };
                return <div style={customStyles} />;
              }}
            </TodayMarker>
          </TimelineMarkers>
          <TimelineHeaders>
            <DateHeader
              unit="year"
              labelFormat={(timeRange, _unit) =>
                DateTime.fromJSDate(timeRange[0].toDate()).setLocale(locale).toFormat('yyyy')
              }
            />

            <DateHeader
              unit="month"
              labelFormat={(timeRange, _unit) =>
                DateTime.fromJSDate(timeRange[0].toDate()).setLocale(locale).toFormat(monthFormat)
              }
            />
            {timelineUnit === null || timelineUnit !== 'day' ? null : (
              <DateHeader
                unit="day"
                labelFormat={(timeRange, _unit) =>
                  DateTime.fromJSDate(timeRange[0].toDate()).setLocale(locale).toFormat('dd')
                }
              />
            )}
          </TimelineHeaders>
        </Timeline>
      </section>
    );
  }
);

TimelineView.displayName = 'TimelineView';
